/**
 * QuadrantPicker — shared 2×2 priority bucket selector.
 *
 * Used by both CouncilTriage (inline quick-pick) and PriorityTab
 * (full detail with scoring criteria). Canonical tier keys use hyphens:
 * 'major-unlocks', 'high-priority', 'quick-wins', 'parking-lot'.
 */

import { makeStyles, tokens } from '@fluentui/react-components'

/** The three buckets a user can WRITE. `parking-lot` is not one of them:
 * #988 §7 made it DERIVED from the parking stage, and the backend 400s any
 * attempt to store it — a variable of this type cannot even carry the value,
 * so the next left-behind write path fails to compile instead of at runtime
 * (#1156). */
export type StoredPriorityTierKey = 'major-unlocks' | 'high-priority' | 'quick-wins'

/** What the 2×2 DISPLAYS — parking-lot renders as state, not a choice. */
export type PriorityTierKey = StoredPriorityTierKey | 'parking-lot'

export const PRIORITY_TIERS: ReadonlyArray<{
  key: PriorityTierKey
  label: string
  caption: string
}> = [
  { key: 'major-unlocks', label: 'Major Unlocks', caption: 'High benefit · Low effort' },
  { key: 'high-priority', label: 'High Priority', caption: 'High benefit · High effort' },
  { key: 'quick-wins', label: 'Quick Wins', caption: 'Low benefit · Low effort' },
  { key: 'parking-lot', label: 'Parking Lot', caption: 'Low benefit · High effort' },
]

/** Normalize underscore/mixed-case keys from legacy data to canonical form. */
export function canonicalizeTier(raw?: string | null): PriorityTierKey | null {
  if (!raw) return null
  const k = raw.toLowerCase().replace(/_/g, '-').trim()
  if (
    k === 'major-unlocks' ||
    k === 'high-priority' ||
    k === 'quick-wins' ||
    k === 'parking-lot'
  ) {
    return k as PriorityTierKey
  }
  return null
}

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalS,
  },
  btn: {
    textAlign: 'left',
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: tokens.colorNeutralForeground1,
    transitionProperty: 'border-color, background, transform',
    transitionDuration: '120ms',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    position: 'relative',
    ':hover': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      transform: 'translateY(-1px)',
    },
  },
  btnActive: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    background: tokens.colorBrandBackground2,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  caption: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  check: {
    position: 'absolute',
    top: '6px',
    right: '8px',
    fontWeight: 700,
    fontSize: '13px',
    opacity: 0.9,
  },
  btnDisabled: {
    cursor: 'default',
    opacity: 0.55,
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke2,
      borderRightColor: tokens.colorNeutralStroke2,
      borderBottomColor: tokens.colorNeutralStroke2,
      borderLeftColor: tokens.colorNeutralStroke2,
      transform: 'none',
    },
  },
})

interface QuadrantPickerProps {
  value: PriorityTierKey | null
  onChange: (tier: PriorityTierKey | null) => void
  /** Show abbreviated hints (for inline/compact use) or full captions */
  compact?: boolean
  /** Quadrants rendered as visible STATE rather than offered as a choice —
   * e.g. `['parking-lot']` where parking derives from the stage (#1156). */
  disabledKeys?: ReadonlyArray<PriorityTierKey>
}

export function QuadrantPicker({ value, onChange, compact, disabledKeys }: QuadrantPickerProps) {
  const styles = useStyles()
  return (
    <div className={styles.grid} role="radiogroup" aria-label="Priority bucket">
      {PRIORITY_TIERS.map((tier) => {
        const active = value === tier.key
        const disabled = disabledKeys?.includes(tier.key) ?? false
        return (
          <button
            key={tier.key}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            data-testid={`priority-${tier.key}`}
            className={`${styles.btn} ${active ? styles.btnActive : ''} ${disabled ? styles.btnDisabled : ''}`}
            onClick={disabled ? undefined : () => onChange(active ? null : tier.key)}
          >
            <span className={styles.label}>{tier.label}</span>
            {!compact && <span className={styles.caption}>{tier.caption}</span>}
            {active && <span className={styles.check}>✓</span>}
          </button>
        )
      })}
    </div>
  )
}
